import { useCallback } from 'react';
import type { MutableRefObject } from 'react';
import { notifications } from '@mantine/notifications';
import { broadcastPosRefresh } from '../../../services/pos-stream/pos-stream.service';
import { isPosSafeZone, type PosSafeZoneState } from '../core/refresh-safe-zone';
import { useDeferredRefresh, type RefreshSource } from './useDeferredRefresh';
import { REFRESH_BROADCAST_FAILED_TOAST, REFRESH_INITIATOR_KEY } from './refresh.toasts';

type UsePosRefreshParams = {
  safeZone: PosSafeZoneState;
  tenantId: string;
  employeeSession: string | null;
  isManager: boolean;
  broadcastInFlightRef: MutableRefObject<boolean>;
  requestRefreshRef: MutableRefObject<(source: RefreshSource) => void>;
  broadcastRefreshRef: MutableRefObject<() => void>;
};

// Architectural summary: composes the safe-zone gate + manager broadcast, off the POS god-file.
export function usePosRefresh({
  safeZone,
  tenantId,
  employeeSession,
  isManager,
  broadcastInFlightRef,
  requestRefreshRef,
  broadcastRefreshRef,
}: UsePosRefreshParams): { requestRefresh: (source: RefreshSource) => void } {
  const { requestRefresh } = useDeferredRefresh(isPosSafeZone(safeZone));
  requestRefreshRef.current = requestRefresh;

  // Manager fans out the refresh; same-tick lock blocks re-clicks (Rule 44).
  const handleBroadcastRefresh = useCallback(async () => {
    if (!employeeSession || !isManager || broadcastInFlightRef.current) {
      return;
    }
    broadcastInFlightRef.current = true;
    // Mark this device as the initiator BEFORE the POST — its own SSE may land before the response.
    try {
      sessionStorage.setItem(REFRESH_INITIATOR_KEY, String(Date.now()));
    } catch {
      // sessionStorage unavailable: worst case the initiator sees the regular receiver toasts.
    }
    try {
      await broadcastPosRefresh(tenantId, employeeSession);
    } catch {
      try {
        sessionStorage.removeItem(REFRESH_INITIATOR_KEY);
      } catch {
        // best-effort cleanup only
      }
      notifications.show(REFRESH_BROADCAST_FAILED_TOAST);
    } finally {
      broadcastInFlightRef.current = false;
    }
  }, [employeeSession, isManager, tenantId, broadcastInFlightRef]);

  // Filled during render (like requestRefreshRef) so a first click before effects run isn't a no-op.
  broadcastRefreshRef.current = () => {
    void handleBroadcastRefresh();
  };

  return { requestRefresh };
}
